import { CurrencyPipe, Location, NgIf, NgFor, PercentPipe } from '@angular/common';
import { Component } from '@angular/core';
import { faAngleLeft, faFileDownload } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { InvoiceService } from '../invoice.service';
import { ReportService } from '../report.service';
import { ExcelDownloaderService } from '../shared/services/excel-downloader.service';
import { ReactiveFormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-reports-sales-tax-invoiced',
    templateUrl: './reports-sales-tax-invoiced.component.html',
    styleUrls: ['./reports-sales-tax-invoiced.component.css'],
    imports: [NgIf, NgbTooltip, FaIconComponent, NgFor, ReactiveFormsModule, PercentPipe, CurrencyPipe]
})
export class ReportsSalesTaxInvoicedComponent {
  // constants
  QUARTERS = [
    'Quarter 1',
    'Quarter 2',
    'Quarter 3',
    'Quarter 4',
  ];
  MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  // Properties
  startDate: string;
  endDate: string;

  salesTaxMap: Map<number, { name: string, amount: number, tax: number }>;
  salesTaxTotal: number = 0;
  salesAmountTotal: number = 0;
  quarterSalesTaxTotal: number = 0;
  quarterSalesAmountTotal: number = 0;

  quarterlySalesTaxMap: Map<number, any[]>;
  monthlySalesTaxMap: Map<number, any[]>;
  salesTaxArr: any[] = [];
  yearMap: any[] = [];
  quarterMap: any[] = [];
  monthlyMap: any[] = [];

  selectedYear: number;
  years: number[] = [];

  labels: string[] = [];

  // Font Awesome Properties
  faAngleLeft = faAngleLeft;
  faFileDownload = faFileDownload;
  isLoading: boolean = true;

  constructor(private reportService: ReportService,
    private invoiceService: InvoiceService,
    private excelDownloaderService: ExcelDownloaderService,
    private currencyPipe: CurrencyPipe,
    public location: Location) { }

  ngOnInit(): void {
    this.getDistinctInvoiceIssueDateYears();
  }

  // Year Selected
  yearSelected(year: number): void {
    this.selectedYear = year;
    this.getInvoicedSalesTaxReport();
  }

  // Get Distinct Invoice Issue Date Years
  private getDistinctInvoiceIssueDateYears(): void {
    this.invoiceService.getDistinctInvoiceIssueDateYears().subscribe((years) => {
      this.years = years;
      if (this.years.length > 0) {
        const currentYear = dayjs().year();
        this.selectedYear = this.years.includes(currentYear) ? currentYear : this.years[0];
        this.getInvoicedSalesTaxReport();
      }
    });
  }

  // Get Invoiced Sales Tax Report
  private getInvoicedSalesTaxReport(): void {
    this.reportService.getInvoicedSalesTaxReport({ year: this.selectedYear }).subscribe((reportData) => {
      this.salesTaxMap = new Map(reportData.salesTaxMap);
      this.salesTaxArr = Array.from(this.salesTaxMap);
      this.yearMap = this.getYearTaxMap(reportData.salesTaxMap)

      this.salesTaxTotal = this.yearMap.reduce((acc, entry) => { return acc + entry.tax; }, 0);
      this.salesAmountTotal = this.yearMap.reduce((acc, entry) => { return acc + entry.amount; }, 0);

    });
    this.reportService.getInvoicedSalesTaxReportQuarter({ year: this.selectedYear }).subscribe((reportData) => {
      this.quarterlySalesTaxMap = new Map(reportData.quarterlySalesTaxMap);
      this.quarterMap = this.getQuarterTaxMap(reportData.quarterlySalesTaxMap)
    });
    this.reportService.getInvoicedSalesTaxReportMonth({ year: this.selectedYear }).subscribe((reportData) => {
      this.monthlyMap = this.getMonthTaxMap(reportData.monthlySalesTaxMap)
      this.monthlySalesTaxMap = new Map(reportData.monthlySalesTaxMap);

      this.isLoading = false;
    });
  }

  getSalesTotal(taxMap: any, order: number, key: string) {
    if (!taxMap.has(order)) return 0;
    const reduceAmnt = taxMap.get(order).reduce((acc, entry) => { return acc + entry[key]; }, 0);
    return this.currencyPipe.transform(reduceAmnt, 'USD', 'symbol');
  }

  getYearTaxMap(rawData) {
    return rawData.map((data) => {
      const combinedByTax = this.reducedMergedByTaxMultiplier(data[1], 'name')

      return {
        label: combinedByTax[0].name,
        amount: combinedByTax[0].amount ? combinedByTax[0].amount : 0,
        tax: combinedByTax[0].tax ? combinedByTax[0].tax : 0,
        taxMultiplier: combinedByTax[0].taxMultiplier,
        year: this.selectedYear
      }
    })
  }

  getMonthTaxMap(rawData) {
    let monthlyFormat = [[], [], [], [], [], [], [], [], [], [], [], []];
    return monthlyFormat.map((raw, index) => {
      const rawArr = rawData.filter(data => data[0] == index + 1);
      if (rawArr.length == 0) {
        return [];
      } else {
        const combinedByTax = this.reducedMergedByTaxMultiplier(rawArr[0][1], 'name')
        return combinedByTax.map(data => {
          return {
            label: data.name,
            amount: data.amount ? data.amount : 0,
            tax: data.tax ? data.tax : 0,
            month: rawArr[0][0]
          }
        });
      }
    })
  }

  getQuarterTaxMap(rawData) {
    let quartersFormat = [[], [], [], []];

    quartersFormat = quartersFormat.map((raw, index) => {
      const rawArr = rawData.filter(data => data[0] == index + 1);
      if (rawArr.length == 0) {
        return [];
      } else {
        const combinedByTax = this.reducedMergedByTaxMultiplier(rawArr[0][1], 'name')
        return combinedByTax.map(data => {
          const quarter = {
            label: data.name,
            amount: data.amount,
            tax: data.tax,
            quarter: rawArr[0][0]
          };
          return quarter
        });
      }
    })

    let max = Math.max(...quartersFormat.map(i => i.length))
    let collatedDatasets = [];

    let patternedArr = [];

    quartersFormat.forEach(element => {
      patternedArr = patternedArr.concat(element)
    });


    quartersFormat.forEach((qtr, index) => {
      for (let i = 0; i < this.yearMap.length; i++) {
        const qtrPresent = qtr.length == 0 ? [] : qtr.filter(qtrLbl => qtrLbl.label == patternedArr[i].label)
        if (qtrPresent.length != 0) {
          collatedDatasets.push(qtrPresent[0])
        } else {
          collatedDatasets.push({
            label: patternedArr[i].label,
            amount: 0,
            quarter: index + 1,
            tax: 0
          })
        }
      }
    })


    this.labels = Array.from(new Set(collatedDatasets.map(data => data.label)));

    return quartersFormat;
  }

  filterMultiArrByLabelAndKey = (arr: any[], label: string, key: string, divisionKey, divisionVal) => {
    const val = arr.filter(item => item.label == label && item[divisionKey] == divisionVal);
    return val && val.length != 0 ? this.currencyPipe.transform(val[0][key], 'USD', 'symbol') : 0
  }

  exportAll() {
    const filename = `Sales_tax_invoiced_${this.selectedYear}`;

    const occurence = ['year', 'quarter', 'month'];
    let multiExport = [];

    occurence.forEach(occur => {
      multiExport.push({
        label: occur.toLocaleUpperCase(),
        values: this.exportReport(occur, false)
      })
    });

    this.excelDownloaderService.exportMultiple(multiExport, filename);
  }

  exportReport(occurence: string, isSingle = true) {
    const keys = ['tax', 'amount']
    let headers = [];
    let filename = `Sales_tax_invoiced_${this.selectedYear}_${occurence}`;
    let data = [];
    let arr = [];
    let taxMap = null;
    const labels = [...this.labels, 'Total'];
    switch (occurence) {
      case 'year':
        headers = ['', this.selectedYear];
        arr = this.yearMap;
        taxMap = Array.from(this.salesTaxMap)
        break;
      case 'quarter':
        headers = ['', ...this.QUARTERS];
        arr = this.quarterMap;
        taxMap = this.quarterlySalesTaxMap;
        break;
      case 'month':
        headers = ['', ...this.MONTHS];
        arr = this.monthlyMap;
        taxMap = this.monthlySalesTaxMap;
        break;
    }

    labels.forEach((label, labelIndex) => {
      let rawData = {};
      headers.forEach((header, index) => {
        keys.forEach(key => {
          if (labelIndex + 1 != labels.length) {
            rawData[index != 0 ? `${header} (${key})` : header] = index != 0 ? this.filterMultiArrByLabelAndKey(occurence == 'year' ? arr : arr[index - 1], label, key, occurence, occurence == 'year' ? this.selectedYear : index)
              : label;
          } else {
            rawData[index != 0 ? `${header} (${key})` : header] = index == 0 ? label : occurence != 'year' ? this.getSalesTotal(taxMap, index, key) : taxMap.reduce((acc, entry) => { return acc + entry[1][key]; }, 0)
          }
        });
      });
      data.push(rawData);
    });
    if (isSingle) this.excelDownloaderService.export(data, filename, occurence.toLocaleUpperCase());
    return data;
  }

  reducedMergedByTaxMultiplier = (arr: any[], keyRef: string) => {
    return arr.reduce((acc, curr) => {
      const index = acc.findIndex(item => item[keyRef] === curr[keyRef])
      if (index > -1) {
        acc[index].amount += curr.amount;
        acc[index].tax += curr.tax;
      } else {
        acc.push({
          ...curr,
          [keyRef]: curr[keyRef],
          amount: curr.amount,
          tax: curr.tax
        })
      }
      return acc
    }, [])
  }
}
